import { useCallback, useContext, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Loading from "../../Loading";
import AccountNumber from "../../components/AccountNumber";
import PerformancePeriodSelector from "../../components/PerformancePeriodSelector";
import { custodianCodeToDisplayName } from "../../lib/custodian";
import { unassignedLabel } from "../../lib/display";
import { useFeatureToggle } from "../../lib/featureToggles";
import { useQueryHousehold } from "../household/lib";
import {
  PerformanceSelection,
  figurePeriodParams,
  useQueryPerformanceFigure,
} from "../performanceFigure";
import PerformanceDrivers from "../performance-drivers/PerformanceDrivers";
import AccountHistoricalAumChart from "./AccountHistoricalAumChart";
import AccountHistoricalPerformanceChart from "./AccountHistoricalPerformanceChart";
import { AccountContext } from "./AccountInfo";
import AccountPerformance from "./AccountPerformance";
import AccountTaxSummary from "./AccountTaxSummary";
import AccountTaxRegistrationField from "./AccountTaxRegistrationField";

const AccountOverview = () => {
  const showPerformanceDrivers = useFeatureToggle("performance-drivers");
  const { account } = useContext(AccountContext);

  const {
    isFetching: isFetchingHousehold,
    isError: isErrorHousehold,
    data: household,
  } = useQueryHousehold(account?.householdId ?? 0, {
    enabled: account?.householdId != null,
  });

  const reportTypes = ["AUM", "Cash", "Performance"];
  const [reportType, setReportType] = useState("AUM");
  const [customAccountReportPeriodParams, setCustomAccountReportPeriodParams] =
    useState("");
  const [reportSelection, setReportSelection] =
    useState<PerformanceSelection>();

  // The Reports charts read over the dates of the figure for their period, as
  // the Performance panel does (R73); the selector dates when it fails.
  const { figure: reportFigure, isPending: isPendingReportFigure } =
    useQueryPerformanceFigure("accounts", account?.id, reportSelection);
  const reportPeriodParams = figurePeriodParams(
    reportFigure,
    customAccountReportPeriodParams,
  );

  const onChange = useCallback((ev: React.ChangeEvent<HTMLSelectElement>) => {
    setReportType(ev.currentTarget.value);
  }, []);

  return typeof account === "undefined" ? null : (
    <>
      <Row>
        <Col xxl={4} xl={6} className="mb-3">
          <dl>
            <Row>
              <Col as="dt" sm={4}>
                Type
              </Col>
              <Col as="dd">{account.type}</Col>
            </Row>
            <Row>
              <Col as="dt" sm={4}>
                Tax Classification
              </Col>
              <Col as="dd">
                <AccountTaxRegistrationField
                  account={account}
                  accountId={account.id}
                />
              </Col>
            </Row>
            <Row>
              <Col as="dt" sm={4}>
                Account Number
              </Col>
              <Col as="dd">
                <AccountNumber
                  fullNumber={account.number}
                  displayNumber={account.displayNumber}
                />
              </Col>
            </Row>
            <Row>
              <Col as="dt" sm={4}>
                Custodian
              </Col>
              <Col as="dd">{custodianCodeToDisplayName(account.custodian)}</Col>
            </Row>
            {/*
              `archiveDate` is null on an account that is not archived. The
              null has to be tested before the comparison, not left to it:
              `null <= new Date()` coerces the null to 0 and is true, which
              would show this row for every unarchived account and then call
              `toLocaleDateString` on the null.
            */}
            {account.archiveDate !== null &&
              account.archiveDate <= new Date() && (
                <Row>
                  <Col as="dt" sm={4}>
                    Archive Date
                  </Col>
                  <Col as="dd">{account.archiveDate.toLocaleDateString()}</Col>
                </Row>
              )}
            <Row>
              <Col as="dt" sm={4}>
                Household
              </Col>
              <Col as="dd">
                {isFetchingHousehold ? (
                  <Loading />
                ) : isErrorHousehold ? (
                  <span className="text-danger">Failed to load household</span>
                ) : typeof household === "undefined" ? (
                  unassignedLabel
                ) : (
                  <Link to={`/clients/households/${account.householdId}`}>
                    {household.name}
                  </Link>
                )}
              </Col>
            </Row>
          </dl>
        </Col>
        <Col xl={4} className="mb-3">
          <AccountPerformance />
        </Col>
        {!account.isTaxable ? null : (
          <Col xl={4} className="mb-3">
            <AccountTaxSummary />
          </Col>
        )}
      </Row>
      <Row>
        <Col xxl={5} xl={6} lg={3} md={4}>
          <h5>Reports</h5>
        </Col>
        <Col xl={4} sm={4}>
          <Form.Select value={reportType} onChange={onChange}>
            {reportTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col>
          <PerformancePeriodSelector
            setCustomPeriodParams={setCustomAccountReportPeriodParams}
            onSelectionChange={setReportSelection}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          {/* No chart read with the selector dates before the figure is in. */}
          {isPendingReportFigure && <Loading message="Chart" />}
          {!isPendingReportFigure && reportType === "AUM" && (
            <AccountHistoricalAumChart
              valueType="totalValue"
              customReportPeriodParams={reportPeriodParams}
            />
          )}
          {!isPendingReportFigure && reportType === "Cash" && (
            <AccountHistoricalAumChart
              valueType="cashValue"
              customReportPeriodParams={reportPeriodParams}
            />
          )}
          {!isPendingReportFigure && reportType === "Performance" && (
            <AccountHistoricalPerformanceChart
              customReportPeriodParams={reportPeriodParams}
            />
          )}
        </Col>
      </Row>
      {showPerformanceDrivers && (
        <PerformanceDrivers
          key={`accounts:${account.id}`}
          kind="accounts"
          id={account.id}
          name={account.displayName}
        />
      )}
    </>
  );
};

export default AccountOverview;
